import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  formatDateKey,
  getTeamAvailabilityDayClass,
  getTeamDayAvailabilityTitle,
  isTeamAvailableForWork
} from '../../utils/workerAvailabilityUtils';

const MONTH_NAMES = [
  'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
  'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
];

const DAY_HEADERS = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

const TeamAvailabilityCalendar = ({ team, workers = [] }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const monthCells = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  if (!team?.members?.length) {
    return (
      <div className="rounded-lg border border-gray-600 bg-gray-700/40 p-3 text-xs text-gray-400">
        Dodajte članove tima da biste videli kalendar dostupnosti.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-600 bg-gray-700/40 p-3 sm:p-4">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="p-1.5 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
          aria-label="Prethodni mesec"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold text-white">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </p>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="p-1.5 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
          aria-label="Sledeći mesec"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_HEADERS.map((label) => (
          <div key={label} className="text-center text-[10px] text-gray-400 font-medium py-1">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {monthCells.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }
          const dateKey = formatDateKey(date);
          const available = isTeamAvailableForWork(team, workers, date);
          const isToday = dateKey === formatDateKey(today);

          return (
            <div
              key={dateKey}
              className={`aspect-square rounded-md text-xs font-semibold flex items-center justify-center ${getTeamAvailabilityDayClass(
                available
              )} ${isToday ? 'outline outline-1 outline-white/50' : ''}`}
              title={getTeamDayAvailabilityTitle(team, workers, date)}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-gray-400">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-emerald-600/70" />
          Tim aktivan (svi članovi dostupni)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-slate-500/80" />
          Tim nedostupan (odmor, bolovanje ili slobodan dan člana)
        </span>
        <span className="text-gray-500 w-full">
          Kalendar je samo za pregled — izračunat je iz kalendara svih članova tima.
        </span>
      </div>
    </div>
  );
};

export default TeamAvailabilityCalendar;
